'use client';

import { useState } from 'react';
import Link from 'next/link';
import { withIndefiniteArticle } from '@paction-2.0/util/formatters';
import styles from './breedFAQ.module.scss';

export interface BreedFAQProps {
  breedName: string;
  healthScreenings?: string[];
  purchaseRange?: { minStr: string; maxStr: string } | null;
}

interface FAQItem {
  question: string;
  /** Plain text answer for structured data */
  answerText: string;
  /** React node for rendering (can include links) */
  answerNode: React.ReactNode;
}

type BreedFAQOptions = {
  healthScreenings?: string[];
  purchaseRange?: { minStr: string; maxStr: string } | null;
};

/** Returns "a" or "an" based on whether the word starts with a vowel sound. */
function aOrAn(word: string): string {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

/** Join a list into a natural "a, b, and c" phrase. */
function formatList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * Generate breed-specific FAQ items for the dog breed guide pages.
 * Questions and answers are designed to match common search queries
 * and provide genuine value to prospective dog owners. When breed data
 * is available, the cost and health answers are enriched with a
 * breed-specific price range and named health screenings.
 */
export function generateBreedFAQItems(
  breedName: string,
  options: BreedFAQOptions = {}
): FAQItem[] {
  const article = aOrAn(breedName);

  const { purchaseRange, healthScreenings } = options;

  const costLead = purchaseRange
    ? `In Canada, ${breedName} puppies from reputable breeders typically cost between ${purchaseRange.minStr} and ${purchaseRange.maxStr} — but the puppy price is only the beginning. `
    : '';

  const namedScreenings =
    healthScreenings && healthScreenings.length > 0
      ? formatList(healthScreenings.slice(0, 4))
      : null;
  const healthLead = namedScreenings
    ? `For ${breedName}, responsible breeders commonly screen breeding pairs using tests such as ${namedScreenings}. `
    : '';

  return [
    {
      question: `What questions should I ask ${withIndefiniteArticle(breedName)} breeder?`,
      answerText: `When you talk to a breeder, you're not just asking "do you have available ${breedName} puppies?" You're looking for a match in the process of rehoming a dog. As a prospective dog owner, you are assessing how predictable the dog's health, temperament, and long-term welfare will be and whether the breeder's practices reduce avoidable risk. You should engage in a conversation about the following topics: Which health tests and genetic screening do you do specifically for ${breedName}, and can I see the results? What do you do if a puppy develops a serious genetic condition? What contract, policies and support do you offer as a breeder? How are puppies raised from birth to 8+ weeks? Ask about socialization, philosophies on early neurological development, handling, and exposure.`,
      answerNode: (
        <>
          <p>
            When you talk to a breeder, you&apos;re not just asking &ldquo;do
            you have available {breedName} puppies?&rdquo; You&apos;re looking
            for a match in the process of rehoming a dog. As a prospective dog
            owner, you are assessing how predictable the dog&apos;s health,
            temperament, and long-term welfare will be and whether the
            breeder&apos;s practices reduce avoidable risk.
          </p>
          <p>You should engage in a conversation about the following topics:</p>
          <ul>
            <li>
              Which health tests and genetic screening do you do specifically
              for {breedName}, and can I see the results?
            </li>
            <li>
              What do you do if a puppy develops a serious genetic condition?
              What contract, policies and support do you offer as a breeder?
            </li>
            <li>
              How are puppies raised from birth to 8+ weeks? Ask about
              socialization, philosophies on early neurological development,
              handling, and exposure.
            </li>
          </ul>
        </>
      )
    },
    {
      question: `How much does it cost to own ${article} ${breedName}?`,
      answerText: `${costLead}The real cost of a dog isn't just the puppy price. After year one, the cost of a dog includes the predictable annual costs + the unpredictable "risk costs." Your goal is to make the cost legible before you commit, as one of the top causes of surrendering a dog is not being able to afford the dog in later years. To help you assess these costs, Paction has created the Ultimate Cost Calculator. You can also read an article written by a Paction breeder on why a prospective dog owner should focus on the ongoing costs of dog ownership and not "how much" the puppy costs.`,
      answerNode: (
        <>
          {costLead && <p>{costLead}</p>}
          <p>
            The real cost of a dog isn&apos;t just the puppy price. After year
            one, the cost of a dog includes the predictable annual costs + the
            unpredictable &ldquo;risk costs.&rdquo; Your goal is to make the
            cost legible before you commit, as one of the top causes of
            surrendering a dog is not being able to afford the dog in later
            years.
          </p>
          <p>
            To help you assess these costs, Paction has created the{' '}
            <Link href="/dog-cost-calculator">Ultimate Cost Calculator</Link>.
            You can also read{' '}
            <Link href="/article/cost-of-dogs-why-breeder-matters">
              an article written by a Paction breeder
            </Link>{' '}
            on why a prospective dog owner should focus on the ongoing costs of
            dog ownership and not &ldquo;how much&rdquo; the puppy costs.
          </p>
        </>
      )
    },
    {
      question: `What health problems are common in ${breedName}?`,
      answerText: `${healthLead}Every dog breed has certain health issues that tend to occur more often, but what matters most is how predictable and preventable those risks are. For many breeds, common concerns include inherited conditions (such as orthopedic, eye, or cardiac issues) as well as lifestyle-related challenges like weight management, dental health, skin or ear problems, and anxiety. Responsible breeders help reduce inherited risk through appropriate health testing, careful selection of breeding pairs, and transparency about family history. To understand more about responsible breeding programs that prioritize health and genetic screening of breeding pairs, check out Making Science Simple: Canine Genetics for Puppy Shoppers. As an owner, you play an equally important role. Consistent preventive veterinary care, proper exercise and enrichment, healthy nutrition, and early screening can significantly improve long-term health outcomes.`,
      answerNode: (
        <>
          {healthLead && <p>{healthLead}</p>}
          <p>
            Every dog breed has certain health issues that tend to occur more
            often, but what matters most is how predictable and preventable
            those risks are. For many breeds, common concerns include inherited
            conditions (such as orthopedic, eye, or cardiac issues) as well as
            lifestyle-related challenges like weight management, dental health,
            skin or ear problems, and anxiety.
          </p>
          <p>
            Responsible breeders help reduce inherited risk through appropriate
            health testing, careful selection of breeding pairs, and
            transparency about family history. To understand more about
            responsible breeding programs that prioritize health and genetic
            screening of breeding pairs, check out{' '}
            <Link href="/canine-genetics-for-puppy-shoppers">
              Making Science Simple: Canine Genetics for Puppy Shoppers
            </Link>
            .
          </p>
          <p>
            As an owner, you play an equally important role. Consistent
            preventive veterinary care, proper exercise and enrichment, healthy
            nutrition, and early screening can significantly improve long-term
            health outcomes.
          </p>
        </>
      )
    },
    {
      question: `Is ${article} ${breedName} right for me?`,
      answerText: `Whether ${article} ${breedName} is right for you depends less on the breed itself and more on how well its typical energy level, temperament, and care needs align with your daily life. Important factors include your schedule and activity level, living environment, household dynamics (such as children, other pets, or allergies), and tolerance for noise or independence. Long-term fit also comes down to maintenance like grooming, training needs, and how often you travel or rely on outside dog care. Use Paction's Dog Breed Matchmaker and Pup Time Estimator to find out more about what dog is right for you. If you're thinking about a new dog after losing one, Are You Ready After Loss? is a gentle check-in to help you decide when the time is right.`,
      answerNode: (
        <>
          <p>
            Whether {article} {breedName} is right for you depends less on the
            breed itself and more on how well its typical energy level,
            temperament, and care needs align with your daily life. Important
            factors include your schedule and activity level, living
            environment, household dynamics (such as children, other pets, or
            allergies), and tolerance for noise or independence. Long-term fit
            also comes down to maintenance like grooming, training needs, and
            how often you travel or rely on outside dog care.
          </p>
          <p>
            Use Paction&apos;s{' '}
            <Link href="/dog-breed-matchmaker">Dog Breed Matchmaker</Link> and{' '}
            <Link href="/pup-time-estimator">Pup Time Estimator</Link> to find
            out more about what dog is right for you.
          </p>
          <p>
            If you&apos;re thinking about a new dog after losing one,{' '}
            <Link href="/pet-loss-readiness">Are You Ready After Loss?</Link> is
            a gentle check-in to help you decide when the time is right.
          </p>
        </>
      )
    }
  ];
}

/**
 * BreedFAQ component displays breed-specific FAQ content for dog breed guide pages.
 * Designed to capture long-tail search traffic and provide genuine value to users.
 */
export function BreedFAQ({
  breedName,
  healthScreenings,
  purchaseRange
}: BreedFAQProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const faqItems = generateBreedFAQItems(breedName, {
    healthScreenings,
    purchaseRange
  });

  const toggleItem = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section className={styles.breedFaq} aria-labelledby="breed-faq-title">
      <h2 id="breed-faq-title" className={styles.title}>
        Frequently Asked Questions About {breedName}
      </h2>
      <div className={styles.faqList}>
        {faqItems.map((item, index) => (
          <div
            key={index}
            className={`${styles.faqItem} ${expandedIndex === index ? styles.faqItemExpanded : ''}`}
          >
            <button
              type="button"
              className={styles.questionButton}
              onClick={() => toggleItem(index)}
              aria-expanded={expandedIndex === index}
              aria-controls={`breed-faq-answer-${index}`}
            >
              <h3 className={styles.question}>{item.question}</h3>
              <span className={styles.chevron} aria-hidden="true" />
            </button>
            <div
              id={`breed-faq-answer-${index}`}
              className={styles.answer}
              hidden={expandedIndex !== index}
            >
              {item.answerNode}
            </div>
          </div>
        ))}
      </div>

      {/* JSON-LD FAQ structured data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answerText
              }
            }))
          })
        }}
      />
    </section>
  );
}
